import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ChevronDown, LogOut } from "lucide-react";
import { TenantProvider, useTenant } from "@/lib/tenant";
import { AuthProvider, useAuth } from "@/lib/auth";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Designer from "@/pages/Designer";
import Settings from "@/pages/Settings";
import Logs from "@/pages/Logs";
import Addin from "@/pages/Addin";
import Banners from "@/pages/Banners";
import Tenants from "@/pages/Tenants";
import Media from "@/pages/Media";
import Fleet from "@/pages/fleet/Fleet";
import FleetMachine from "@/pages/fleet/FleetMachine";
import FleetMap from "@/pages/fleet/FleetMap";
import FleetConnect from "@/pages/fleet/FleetConnect";
import FleetOperations from "@/pages/fleet/FleetOperations";
import FleetFaults from "@/pages/fleet/FleetFaults";
import Operators from "@/pages/admin/Operators";
import AdminDiary from "@/pages/admin/AdminDiary";
import XeroConnect from "@/pages/admin/XeroConnect";
import Machines from "@/pages/admin/Machines";
import Jobs from "@/pages/admin/Jobs";
import OperatorLock from "@/pages/operator/OperatorLock";
import OperatorHome from "@/pages/operator/OperatorHome";
import OperatorAdmin from "@/pages/operator/OperatorAdmin";
import OperatorNewEntry from "@/pages/operator/OperatorNewEntry";
import OperatorDiary from "@/pages/operator/OperatorDiary";
import OperatorMachines from "@/pages/operator/OperatorMachines";
import OperatorCalendar from "@/pages/operator/OperatorCalendar";
import SilageTracker from "@/pages/operator/SilageTracker";
import { OperatorProvider, useOperator } from "@/lib/operator";
import { TreeAuthProvider, useTreeAuth } from "@/lib/treeAuth";
import TreeLayout from "@/components/trees/TreeLayout";
import TreeLoginPage from "@/pages/trees/TreeLoginPage";
import TreeDashboardPage from "@/pages/trees/TreeDashboardPage";
import TreePalletsPage from "@/pages/trees/TreePalletsPage";
import TreeYardPage from "@/pages/trees/TreeYardPage";
import TreeLoadsPage from "@/pages/trees/TreeLoadsPage";
import TreeFieldPage from "@/pages/trees/TreeFieldPage";
import TreeManifestPage from "@/pages/trees/TreeManifestPage";
import TreeTVDashboard from "@/pages/trees/TreeTVDashboard";
import TreeUsersPage from "@/pages/trees/TreeUsersPage";
import TreeRolesPage from "@/pages/trees/TreeRolesPage";
import TreeSettingsPage from "@/pages/trees/TreeSettingsPage";
import TreeFieldStatusTask from "@/pages/trees/TreeFieldStatusTask";
import TreeFieldCollectorTask from "@/pages/trees/TreeFieldCollectorTask";

// Backwards-compat exports (existing pages import these)
export { API, api } from "@/lib/tenant";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return (
    <div className="min-h-screen bg-[#0b1020] grid place-items-center text-slate-400 text-sm">Loading…</div>
  );
  if (!user) return <Navigate to="/admin/login" state={{ from: loc }} replace />;
  return children;
}

function TenantSwitcher() {
  const { tenants, tenantId, setTenantId, currentTenant } = useTenant();
  const [open, setOpen] = useState(false);
  if (!tenants.length) return null;
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} className="w-full p-3 border-b border-white/5 flex items-center justify-between hover:bg-white/[0.02] transition-colors" data-testid="tenant-switcher">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-[#0a0e1c] font-bold text-xs shrink-0">
            {(currentTenant?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 text-left">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Tenant</div>
            <div className="text-sm font-medium truncate text-slate-100">{currentTenant?.name || "Select tenant"}</div>
          </div>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 bg-[#0a0e1c] border border-white/10 rounded-b-lg shadow-2xl z-50 overflow-hidden">
          {tenants.map(t => (
            <button key={t.id} onClick={() => { setTenantId(t.id); setOpen(false); }}
              data-testid={`switch-tenant-${t.id}`}
              className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 hover:bg-white/[0.05] transition-colors border-l-2 ${t.id === tenantId ? "border-emerald-500 bg-emerald-500/[0.06]" : "border-transparent"}`}>
              <div className="w-6 h-6 rounded bg-gradient-to-br from-emerald-400/40 to-cyan-500/40 flex items-center justify-center text-xs font-bold shrink-0">
                {t.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate">{t.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{t.domain}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  const { tenantId, api } = useTenant();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  useEffect(() => {
    if (!tenantId) { setHealth(null); return; }
    setHealth(null);
    api.get("/health").then(r => setHealth(r.data)).catch(() => setHealth({ status: "error" }));
  }, [tenantId]); // eslint-disable-line

  const handleLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0b1020] text-slate-100 flex">
      <aside className="w-64 border-r border-white/5 bg-[#0a0e1c] flex flex-col">
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-[#0a0e1c] font-black text-lg">S</div>
            <div>
              <div className="text-sm font-semibold tracking-wide">Signature</div>
              <div className="text-xs text-slate-400 -mt-0.5">M365 Manager</div>
            </div>
          </div>
        </div>
        <TenantSwitcher />
        <nav className="p-3 space-y-1 flex-1">
          {[
            { to: "/admin", label: "Users & Deploy", testId: "nav-users" },
            { to: "/admin/addin", label: "Outlook Add-in", testId: "nav-addin" },
            { to: "/admin/designer", label: "Signature Designer", testId: "nav-designer" },
            { to: "/admin/banners", label: "Banner Studio", testId: "nav-banners" },
            { to: "/admin/settings", label: "Brand Settings", testId: "nav-settings" },
            { to: "/admin/logs", label: "Deployment Log", testId: "nav-logs" },
            { to: "/admin/tenants", label: "Tenants", testId: "nav-tenants" },
            { to: "/admin/media", label: "Public Site Media", testId: "nav-media" },
            { to: "/admin/fleet", label: "Fleet (New Holland)", testId: "nav-fleet" },
            { to: "/admin/machines", label: "All Machines", testId: "nav-machines" },
            { to: "/admin/diary", label: "Work Diary", testId: "nav-diary" },
            { to: "/admin/jobs", label: "Plan Jobs", testId: "nav-jobs" },
            { to: "/admin/operators", label: "Operators", testId: "nav-operators" },
            { to: "/admin/xero", label: "Xero Integration", testId: "nav-xero" },
          ].map(item => (
            <NavLink key={item.to} to={item.to} end={item.to === "/admin"} data-testid={item.testId}
              className={({ isActive }) => `block px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "text-slate-300 hover:bg-white/5 hover:text-white border border-transparent"}`}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/5 text-xs space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-slate-400">Signed in</div>
              <div className="text-slate-200 text-[11px] truncate" title={user?.email}>{user?.email}</div>
            </div>
            <button onClick={handleLogout} title="Sign out" data-testid="logout-btn"
                    className="text-slate-400 hover:text-red-300 p-1.5 rounded hover:bg-white/5 transition-colors">
              <LogOut size={14} />
            </button>
          </div>
          <div>
            <div className="text-slate-400 mb-1">Connection</div>
            {health?.status === "ok" ? (
              <div className="text-emerald-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Connected</div>
            ) : health ? <div className="text-red-400">Not connected</div> : <div className="text-slate-500">Checking…</div>}
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">{children}</main>
    </div>
  );
}

function AdminRoutes() {
  return (
    <TenantProvider>
      <Shell>
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="addin" element={<Addin />} />
          <Route path="designer" element={<Designer />} />
          <Route path="banners" element={<Banners />} />
          <Route path="settings" element={<Settings />} />
          <Route path="logs" element={<Logs />} />
          <Route path="tenants" element={<Tenants />} />
          <Route path="media" element={<Media />} />
          <Route path="fleet" element={<Fleet />} />
          <Route path="fleet/connect" element={<FleetConnect />} />
          <Route path="fleet/map" element={<FleetMap />} />
          <Route path="fleet/operations" element={<FleetOperations />} />
          <Route path="fleet/faults" element={<FleetFaults />} />
          <Route path="fleet/:vin" element={<FleetMachine />} />
          <Route path="diary" element={<AdminDiary />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="machines" element={<Machines />} />
          <Route path="operators" element={<Operators />} />
          <Route path="xero" element={<XeroConnect />} />
        </Routes>
      </Shell>
    </TenantProvider>
  );
}

function OperatorApp() {
  const { operator, loading } = useOperator();
  if (loading) return (
    <div className="min-h-screen bg-[#04060d] grid place-items-center text-slate-500 text-sm">Loading…</div>
  );
  if (!operator) return <OperatorLock />;
  return (
    <Routes>
      <Route index element={<OperatorHome />} />
      <Route path="new" element={<OperatorNewEntry />} />
      <Route path="diary" element={<OperatorDiary />} />
      <Route path="diary/:id" element={<OperatorNewEntry />} />
      <Route path="calendar" element={<OperatorCalendar />} />
      <Route path="silage" element={<SilageTracker />} />
      <Route path="machines" element={<OperatorMachines />} />
      <Route path="admin/*" element={<OperatorAdmin />} />
    </Routes>
  );
}

function RequireTreeAdmin({ children }) {
  const { user, loading } = useTreeAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#F4F1ED] grid place-items-center text-[#4A5D53] text-sm">Loading…</div>
  );
  if (!user) return <Navigate to="/trees/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/trees/task" replace />;
  return <TreeLayout>{children}</TreeLayout>;
}

function RequirePage({ page, children }) {
  const { user, loading, permissions } = useTreeAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#F4F1ED] grid place-items-center text-[#4A5D53] text-sm">Loading…</div>
  );
  if (!user) return <Navigate to="/trees/login" replace />;
  if (user.role === 'admin') return <TreeLayout>{children}</TreeLayout>;
  if (user.role === 'field_status' || user.role === 'field_collector') return <Navigate to="/trees/task" replace />;
  if (!permissions?.[page]?.access) {
    return (
      <TreeLayout>
        <div className="text-center py-24 text-[#4A5D53]" data-testid="tree-no-access-message">
          You don't have access to this page yet. Ask an admin to grant it from Roles.
        </div>
      </TreeLayout>
    );
  }
  return <TreeLayout>{children}</TreeLayout>;
}

function TreeTaskGate() {
  const { user, loading } = useTreeAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#F4F1ED] grid place-items-center text-[#4A5D53] text-sm">Loading…</div>
  );
  if (!user) return <Navigate to="/trees/login" replace />;
  if (user.role === 'field_status') return <TreeFieldStatusTask />;
  if (user.role === 'field_collector') return <TreeFieldCollectorTask />;
  return <Navigate to="/trees" replace />;
}

function TreeApp() {
  useEffect(() => {
    const favicon192 = document.querySelector('link[rel="icon"][sizes="192x192"]');
    const favicon512 = document.querySelector('link[rel="icon"][sizes="512x512"]');
    const appleTouch = document.querySelector('link[rel="apple-touch-icon"]');
    const shortcutIcon = document.querySelector('link[rel="shortcut icon"]');
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const original = {
      favicon192: favicon192?.getAttribute('href'),
      favicon512: favicon512?.getAttribute('href'),
      appleTouch: appleTouch?.getAttribute('href'),
      shortcutIcon: shortcutIcon?.getAttribute('href'),
      manifest: manifestLink?.getAttribute('href'),
    };
    favicon192?.setAttribute('href', '/icons/tree-icon-192.png');
    favicon512?.setAttribute('href', '/icons/tree-icon-512.png');
    appleTouch?.setAttribute('href', '/icons/tree-apple-touch-icon.png');
    shortcutIcon?.setAttribute('href', '/icons/tree-apple-touch-icon.png');
    manifestLink?.setAttribute('href', '/trees-manifest.json');
    return () => {
      if (original.favicon192) favicon192?.setAttribute('href', original.favicon192);
      if (original.favicon512) favicon512?.setAttribute('href', original.favicon512);
      if (original.appleTouch) appleTouch?.setAttribute('href', original.appleTouch);
      if (original.shortcutIcon) shortcutIcon?.setAttribute('href', original.shortcutIcon);
      if (original.manifest) manifestLink?.setAttribute('href', original.manifest);
    };
  }, []);

  return (
    <Routes>
      <Route path="login" element={<TreeLoginPage />} />
      <Route path="tv-dashboard" element={<TreeTVDashboard />} />
      <Route path="manifest/:loadId" element={<TreeManifestPage />} />
      <Route path="task" element={<TreeTaskGate />} />
      <Route index element={<RequirePage page="dashboard"><TreeDashboardPage /></RequirePage>} />
      <Route path="field" element={<RequirePage page="field"><TreeFieldPage /></RequirePage>} />
      <Route path="pallets" element={<RequirePage page="stock"><TreePalletsPage /></RequirePage>} />
      <Route path="yard" element={<RequirePage page="yard"><TreeYardPage /></RequirePage>} />
      <Route path="loads" element={<RequirePage page="collections"><TreeLoadsPage /></RequirePage>} />
      <Route path="users" element={<RequireTreeAdmin><TreeUsersPage /></RequireTreeAdmin>} />
      <Route path="roles" element={<RequireTreeAdmin><TreeRolesPage /></RequireTreeAdmin>} />
      <Route path="settings" element={<RequireTreeAdmin><TreeSettingsPage /></RequireTreeAdmin>} />
      <Route path="*" element={<Navigate to="/trees" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/*" element={<RequireAuth><AdminRoutes /></RequireAuth>} />
          <Route path="/app/*" element={<OperatorProvider><OperatorApp /></OperatorProvider>} />
          <Route path="/trees/*" element={<TreeAuthProvider><TreeApp /></TreeAuthProvider>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
